import { IntegrationType } from '@doss/prisma/types'
import * as yup from 'yup'

export const gmailIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.GMAIL] as const)
      .required(),
    emailAddress: yup.string().required(),
    lastHistoryId: yup.string().optional(),
  })
  .strict()

export type GmailIntegrationMetadata = yup.InferType<typeof gmailIntegrationMetadataSchema>

export const shopifyIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.SHOPIFY] as const)
      .required(),
    // The URL of the Shop: i.e. doss-test.myshopify.com
    shopUrl: yup.string().required(),
    // OAuth scopes granted by the merchant
    scopes: yup.array(yup.string().required()).optional(),
    /**
     * When true, this Shopify integration was installed via the Partner Dashboard route — i.e.
     * an unpublished/dev Doss app on a development store — rather than via the public App Store
     * listing. Informational/routing only at install time; Shopify API calls always target
     * `{shopUrl}/admin/api/...` regardless of this flag.
     */
    useSandbox: yup.boolean().optional(),
  })
  .strict()

export type ShopifyIntegrationMetadata = yup.InferType<typeof shopifyIntegrationMetadataSchema>

export const shopeeIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SHOPEE] as const)
    .required(),
  /**
   * Per-integration sandbox toggle (PHP-142). When true, the integration targets Shopee's test
   * environment (`partner.test-stable.shopeemobile.com`) rather than production.
   */
  useSandbox: yup.boolean().optional(),
  /**
   * The authorized Shopee shop id. Captured from the OAuth redirect at authorization time and
   * required to sign every shop-level API request. Optional on the schema because metadata is
   * populated progressively (the value is absent until the shop authorizes); request-signing code
   * throws if it is missing at call time.
   */
  shopId: yup.number().optional(),
})

export type ShopeeIntegrationMetadata = yup.InferType<typeof shopeeIntegrationMetadataSchema>

/**
 * Lazada's region-specific marketplaces. The seller picks their region at install time; it resolves
 * the API gateway host. Docs: https://open.lazada.com/apps/doc/doc?nodeId=10443&docId=108065.
 */
export enum LazadaRegion {
  SINGAPORE = 'SG',
  THAILAND = 'TH',
  MALAYSIA = 'MY',
  VIETNAM = 'VN',
  PHILIPPINES = 'PH',
  INDONESIA = 'ID',
}

export const lazadaIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.LAZADA] as const)
    .required(),
  /** The connected regional marketplace; resolves the API gateway host. Selected in the dialog. */
  region: yup.string().oneOf(Object.values(LazadaRegion)).required(),
  /**
   * The authorized seller id, captured from the token response
   * Optional because it is absent until the seller authorizes.
   */
  sellerId: yup.string().optional(),
})

export type LazadaIntegrationMetadata = yup.InferType<typeof lazadaIntegrationMetadataSchema>

/**
 * Metadata for Quickbooks Integration
 *
 * The user's QuickBooks Online company is identified by the realmId
 */
export const quickbooksIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.QUICKBOOKS] as const)
    .required(),
  realmId: yup.string().required(),
  scopes: yup.array(yup.string().required()).optional(),
  isStaging: yup.boolean().optional(),
})

export type QuickbooksIntegrationMetadata = yup.InferType<
  typeof quickbooksIntegrationMetadataSchema
>

export const rampIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.RAMP] as const)
    .required(),
  scopes: yup.array(yup.string().required()).optional(),
  /** When true, route this integration to the Ramp demo (sandbox) environment instead of production. */
  useSandbox: yup.boolean().optional(),
})

export type RampIntegrationMetadata = yup.InferType<typeof rampIntegrationMetadataSchema>

export const rapidPackIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.RAPID_PACK] as const)
    .required(),
  apiUrl: yup.string().required(),
})

export type RapidPackIntegrationMetadata = yup.InferType<typeof rapidPackIntegrationMetadataSchema>

/** Default LOCAD OpenAPI v2 production host. */
export const LOCAD_DEFAULT_BASE_URL = 'https://dashboard.golocad.com'

/** LOCAD OpenAPI v2 staging host. */
export const LOCAD_STAGING_BASE_URL = 'https://dashboard-stg.golocad.com'

/** LOCAD seller webhook auth header sent on callback POSTs to Doss. */
export const LOCAD_WEBHOOK_AUTH_HEADER_KEY = 'Authorization'

/** Header required on custom webstore API calls (value = Locad connection id). */
export const LOCAD_CONNECTION_ID_HEADER = 'connection-id'

/** LOCAD OpenAPI path version for seller/custom webstore endpoints. */
export const LOCAD_OPENAPI_VERSION = 'v2'

export const locadCallbackSubscriptionMetadataSchema = yup.object({
  callbackType: yup.string().required(),
  subscriptionId: yup.number().integer().required(),
})

export type LocadCallbackSubscriptionMetadata = yup.InferType<
  typeof locadCallbackSubscriptionMetadataSchema
>

export const locadIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.LOCAD] as const)
    .required(),
  /** LOCAD brand id provided by the Locad team; required for JWT obtain. */
  brandId: yup.number().integer().min(1).required(),
  /**
   * Per-integration sandbox toggle. When true, route this integration to Locad staging
   * (`dashboard-stg.golocad.com`) rather than production (`dashboard.golocad.com`).
   */
  useSandbox: yup.boolean().optional(),
  /**
   * Optional OpenAPI base URL escape hatch. When set, overrides production/staging resolution from
   * `useSandbox`.
   */
  baseUrl: yup.string().optional(),
  /** Bearer token LOCAD includes when POSTing webhooks to Doss (auth_header_key / auth_token). */
  webhookAuthToken: yup.string().optional(),
  /** Active LOCAD callback subscription ids returned by POST /openapi/seller/callback_subscriptions/. */
  callbackSubscriptions: yup.array().of(locadCallbackSubscriptionMetadataSchema).optional(),
})

export type LocadIntegrationMetadata = yup.InferType<typeof locadIntegrationMetadataSchema>

/** Default Think Logistics DFS Partner API host (no public sandbox URL documented). */
export const THINK_LOGISTICS_DEFAULT_BASE_URL = 'https://api.thinklogistics.com'

/**
 * Historical callback query param. Intake does not require it. Still redacted from webhook
 * logs when an old Think URL includes it.
 */
export const THINK_LOGISTICS_WEBHOOK_TOKEN_QUERY_PARAM = 'webhookToken'

export const thinkLogisticsIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.THINK_LOGISTICS] as const)
    .required(),
  /** Partner API base URL override — ignored at runtime; stripped on create/update (SSRF / credential exfil). */
  baseUrl: yup.string().optional(),
  /** Think customer id used in Partner API paths (`custId`). */
  custId: yup.string().required(),
  /** Canadian warehouse code used in inventory and PO paths (`whCode`). */
  whCode: yup.string().required(),
})

export type ThinkLogisticsIntegrationMetadata = yup.InferType<
  typeof thinkLogisticsIntegrationMetadataSchema
>

export enum RoutableEnvironment {
  PRODUCTION = 'production',
  SANDBOX = 'sandbox',
}

export const routableIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.ROUTABLE] as const)
    .required(),
  environment: yup.string().oneOf(Object.values(RoutableEnvironment)).required(),
})

export type RoutableIntegrationMetadata = yup.InferType<typeof routableIntegrationMetadataSchema>

export const logiwaIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.LOGIWA] as const)
      .required(),
    /** When true, use sandbox base URL (myapisandbox.logiwa.com). Per Logiwa Open API docs. */
    useSandbox: yup.boolean().optional(),
  })
  .strict()

export type LogiwaIntegrationMetadata = yup.InferType<typeof logiwaIntegrationMetadataSchema>

export const loopReturnsIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.LOOP_RETURNS] as const)
      .required(),
    shopId: yup.number().notRequired(),
    webhooks: yup
      .array()
      .of(
        yup.object({
          type: yup
            .string()
            .oneOf(['created', 'updated'] as const)
            .required(),
          webhook: yup.number().required(),
        }),
      )
      .optional(), // optional if you want to allow creation without them initially
  })
  .strict()

export type LoopReturnsIntegrationMetadata = yup.InferType<
  typeof loopReturnsIntegrationMetadataSchema
>

export const g2mintIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.G2MINT] as const)
    .required(),
})
export type G2MintIntegrationMetadata = yup.InferType<typeof g2mintIntegrationMetadataSchema>

export const gnosisIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.GNOSIS] as const)
      .required(),
  })
  .strict()

export type GnosisIntegrationMetadata = yup.InferType<typeof gnosisIntegrationMetadataSchema>

/**
 * Metadata for Aramex Integration
 *
 * Aramex is a WMS (Warehouse Management System) used for inventory visibility.
 * The facility and storerKey identify the warehouse and customer.
 * Credentials (SSA_Login, SSA_Password) are stored in IntegrationCredential.
 */
export const aramexIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.ARAMEX] as const)
      .required(),
    /** Warehouse facility code (e.g., UAE, Saudi, Singapore) */
    facility: yup.string().required(),
    /** Storer/customer identifier */
    storerKey: yup.string().required(),
    /** API base URL - defaults to production, can be set to test environment */
    baseUrl: yup.string().url().optional(),
  })
  .strict()

export type AramexIntegrationMetadata = yup.InferType<typeof aramexIntegrationMetadataSchema>

export const outlookIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.OUTLOOK] as const)
      .required(),
    emailAddress: yup.string().email().required(),
    scope: yup.array().required(),
    subscriptionId: yup.string().required(),
  })
  .strict()

export type OutlookIntegrationMetadata = yup.InferType<typeof outlookIntegrationMetadataSchema>

export const warehanceIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.WAREHANCE] as const)
      .required(),
  })
  .strict()

export type WarehanceIntegrationMetadata = yup.InferType<typeof warehanceIntegrationMetadataSchema>

export const wooCommerceIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.WOO_COMMERCE] as const)
      .required(),
    storeUrl: yup.string().required(),
  })
  .strict()

export type WooCommerceIntegrationMetadata = yup.InferType<
  typeof wooCommerceIntegrationMetadataSchema
>

export const stripeIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.STRIPE] as const)
      .required(),
    connectedAccountId: yup.string().required(),
    isStaging: yup.boolean().notRequired(),
  })
  .strict()

export type StripeIntegrationMetadata = yup.InferType<typeof stripeIntegrationMetadataSchema>

export const spsCommerceIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.SPS_COMMERCE] as const)
      .required(),
    isStaging: yup.boolean().notRequired(),
    /** SPS Auth0 account email from authorize; optional for integrations created before we stored it */
    emailAddress: yup.string().email().notRequired(),
  })
  .strict()

export type SPSCommerceIntegrationMetadata = yup.InferType<
  typeof spsCommerceIntegrationMetadataSchema
>

export enum StordEnvironment {
  PRODUCTION = 'production',
  STAGING = 'staging',
}

export const stordIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.STORD] as const)
    .required(),
  environment: yup.string().oneOf(Object.values(StordEnvironment)).required(),
})

export type StordIntegrationMetadata = yup.InferType<typeof stordIntegrationMetadataSchema>

export const extensivIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.EXTENSIV] as const)
      .required(),
    customerId: yup.number().required(),
  })
  .strict()

export type ExtensivIntegrationMetadata = yup.InferType<typeof extensivIntegrationMetadataSchema>

export const extronIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.EXTRON] as const)
    .required(),
  /**
   * Per-integration sandbox toggle (PHP-142). When true, the integration targets Extron's test
   * environment rather than production; auth/base-URL resolution lands in the read-integration PR.
   */
  useSandbox: yup.boolean().optional(),
})

export type ExtronIntegrationMetadata = yup.InferType<typeof extronIntegrationMetadataSchema>

export const flexportIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.FLEXPORT] as const)
      .required(),
  })
  .strict()

export type FlexportIntegrationMetadata = yup.InferType<typeof flexportIntegrationMetadataSchema>

export enum FtpProtocol {
  FTP = 'FTP',
  FTPS = 'FTPS',
  SFTP = 'SFTP',
}

export enum SftpAlgorithmProfile {
  MODERN = 'MODERN',
  LEGACY = 'LEGACY',
}

export const ftpIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.FTP] as const)
      .required(),
    host: yup.string().required(),
    port: yup.number().required(),
    protocol: yup.string().oneOf(Object.values(FtpProtocol)).default(FtpProtocol.FTP).required(),
    // Optional: route this integration's FTP/SFTP traffic through a named egress proxy pool
    // so the partner sees a stable, whitelistable source IP. Resolved at runtime against
    // EGRESS_POOLS in Doppler. Undefined = direct connection (default).
    egressPool: yup.string().optional(),
    // Optional SFTP compatibility mode. Undefined/MODERN keeps ssh2 defaults; LEGACY appends
    // the legacy key-exchange algorithm required by older partner servers.
    sftpAlgorithmProfile: yup
      .mixed<SftpAlgorithmProfile>()
      .oneOf(Object.values(SftpAlgorithmProfile))
      .optional(),
    // Legacy field for backward compatibility - will be deprecated
    secure: yup.boolean().optional(),
  })
  .strict()

export type FtpIntegrationMetadata = yup.InferType<typeof ftpIntegrationMetadataSchema>

export const fosdickIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.FOSDICK] as const)
      .required(),
  })
  .strict()

export type FosdickIntegrationMetadata = yup.InferType<typeof fosdickIntegrationMetadataSchema>

export const netsuiteIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.NETSUITE] as const)
    .required(),
  accountId: yup.string().required(),
})

export type NetsuiteIntegrationMetadata = yup.InferType<typeof netsuiteIntegrationMetadataSchema>

export const xeroIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.XERO] as const)
      .required(),
    xeroTenantId: yup.string().optional(),
  })
  .strict()

export type XeroIntegrationMetadata = yup.InferType<typeof xeroIntegrationMetadataSchema>
export const owdIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.ONE_WORLD_DIRECT] as const)
    .required(),
  clientId: yup.string().required(),
  clientAuthCode: yup.string().required(),
  /**
   * When true, route this integration to the OWD test environment instead of production. Flips
   * both the request URL (`/test/api/api.jsp` vs `/api/api.jsp`) and the `@testing` XML attribute.
   */
  useSandbox: yup.boolean().optional(),
})

export type OwdIntegrationMetadata = yup.InferType<typeof owdIntegrationMetadataSchema>

export const orderfulIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.ORDERFUL] as const)
    .required(),
  region: yup
    .string()
    .oneOf(['us', 'eu'] as const)
    .required(),
})

export type OrderfulIntegrationMetadata = yup.InferType<typeof orderfulIntegrationMetadataSchema>

export const postgresIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.POSTGRES] as const)
      .required(),
    host: yup.string().required(),
    port: yup.number().required(),
    database: yup.string().required(),
  })
  .strict()

export type PostgresIntegrationMetadata = yup.InferType<typeof postgresIntegrationMetadataSchema>

export const ingramMicroIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.INGRAM_MICRO] as const)
      .required(),
    customerNumber: yup.string().required(),
    isStaging: yup.boolean().notRequired(),
  })
  .strict()

export type IngramMicroIntegrationMetadata = yup.InferType<
  typeof ingramMicroIntegrationMetadataSchema
>

export const jazzIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.JAZZ] as const)
    .required(),
})
export type JazzIntegrationMetadata = yup.InferType<typeof jazzIntegrationMetadataSchema>

export const jigsawIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.JIGSAW] as const)
    .required(),
})
export type JigsawIntegrationMetadata = yup.InferType<typeof jigsawIntegrationMetadataSchema>

export const tdSynnexIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.TD_SYNNEX] as const)
      .required(),
    accountId: yup.number().required(),
  })
  .strict()

export type TdSynnexIntegrationMetadata = yup.InferType<typeof tdSynnexIntegrationMetadataSchema>

export const tdSynnexXMLIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.TD_SYNNEX_XML] as const)
      .required(),
    isSandbox: yup.boolean().optional(),
  })
  .strict()

export type TdSynnexXMLIntegrationMetadata = yup.InferType<
  typeof tdSynnexXMLIntegrationMetadataSchema
>

/**
 * Enum for the supported Amazon Seller selling regions.
 */
export enum AmazonSellerSellingRegion {
  NORTH_AMERICA = 'NORTH_AMERICA',
  EUROPE = 'EUROPE',
  FAR_EAST = 'FAR_EAST',
}

export const amazonSellerIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.AMAZON_SELLER] as const)
      .required(),
    sellingPartnerId: yup.string().required(),
    sqsDestinationId: yup.string().notRequired(),
    // Defaults to North America if not specified
    sellingRegion: yup
      .string()
      .oneOf(Object.values(AmazonSellerSellingRegion))
      .default(AmazonSellerSellingRegion.NORTH_AMERICA),
    isProd: yup.boolean().notRequired(),
    // Notification subscription ID → the SP-API region it was created in.
    // Routes webhooks when one seller is connected for several regions.
    subscriptionRegions: yup
      .mixed<Partial<Record<string, AmazonSellerSellingRegion>>>()
      .notRequired(),
  })
  .strict()

export type AmazonSellerIntegrationMetadata = yup.InferType<
  typeof amazonSellerIntegrationMetadataSchema
>

export const echoShipIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.ECHO_SHIP] as const)
      .required(),
    username: yup.string().required(),
  })
  .strict()

export type EchoShipIntegrationMetadata = yup.InferType<typeof echoShipIntegrationMetadataSchema>

export const d2IntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.D2] as const)
      .required(),
    custid: yup.string().required(),
  })
  .strict()

export type D2IntegrationMetadata = yup.InferType<typeof d2IntegrationMetadataSchema>

export const billIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.BILL] as const)
      .required(),
    billOrganizationId: yup.string().required(),
    /** When true, route this integration to the Bill.com sandbox API instead of production. */
    useSandbox: yup.boolean().optional(),
  })
  .strict()

export type BillIntegrationMetadata = yup.InferType<typeof billIntegrationMetadataSchema>

export const shipBobIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SHIPBOB] as const)
    .required(),
  scopes: yup.array(yup.string().required()).optional(),
  webhooks: yup.array(yup.string().required()).optional(),
  isProd: yup.boolean().optional(),
})
export type ShipBobIntegrationMetadata = yup.InferType<typeof shipBobIntegrationMetadataSchema>

export const shipMonkIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SHIPMONK] as const)
    .required(),
  /** When true, route this integration to the ShipMonk sandbox API instead of production. */
  useSandbox: yup.boolean().optional(),
})
export type ShipMonkIntegrationMetadata = yup.InferType<typeof shipMonkIntegrationMetadataSchema>

export const shipHeroIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SHIPHERO] as const)
    .required(),
  accountId: yup.string().required(),
})
export type ShipHeroIntegrationMetadata = yup.InferType<typeof shipHeroIntegrationMetadataSchema>

export const hubSpotIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.HUBSPOT] as const)
    .required(),
  portalId: yup.string().required(),
  scope: yup.array(yup.string().required()).required(),
})
export type HubSpotIntegrationMetadata = yup.InferType<typeof hubSpotIntegrationMetadataSchema>

export const dossBankingIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.DOSS_BANKING] as const)
      .required(),
    customerId: yup.string().required(),
  })
  .strict()

export type DossBankingIntegrationMetadata = yup.InferType<
  typeof dossBankingIntegrationMetadataSchema
>

export const dossBillPayIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.DOSS_BILL_PAY] as const)
      .required(),
    organizationId: yup.string().required(),

    // Set while setting up 2FA phone number
    setupId: yup.string().nullable(),
    // Set while confirming 2FA challenge
    challengeId: yup.string().nullable(),
    // Phone number to send 2FA code to
    phoneNumber: yup.string().required(),

    // Set once funding account is created after 2FA is confirmed
    fundingAccountId: yup.string().nullable(),

    /** When true, route this integration to the Bill.com sandbox API instead of production. */
    useSandbox: yup.boolean().optional(),
  })
  .strict()

export type DossBillPayIntegrationMetadata = yup.InferType<
  typeof dossBillPayIntegrationMetadataSchema
>

export const a2bFulfillmentIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.A2B_FULFILLMENT] as const)
    .required(),
})
export type A2bFulfillmentIntegrationMetadata = yup.InferType<
  typeof a2bFulfillmentIntegrationMetadataSchema
>

export const deposcoIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.DEPOSCO] as const)
    .required(),
  tenantCode: yup.string().required(),
  pageNo: yup.number().optional(),
  pageSize: yup.number().optional(),
  businessUnit: yup.string().optional(),
  facilityNumber: yup.string().optional(),
  startActivityTime: yup.string().optional(),
  endActivityTime: yup.string().optional(),
})
export type DeposcoIntegrationMetadata = yup.InferType<typeof deposcoIntegrationMetadataSchema>

export const crstlIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.CRSTL] as const)
    .required(),
  crstlOrganizationId: yup.string().required(),
  /** When true, use Crstl sandbox API; when false, production. Omitted = follow Doss deployment (legacy). */
  useCrstlSandbox: yup.boolean().optional(),
})
export type CrstlIntegrationMetadata = yup.InferType<typeof crstlIntegrationMetadataSchema>

export const cropsterIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.CROPSTER] as const)
    .required(),
  clientId: yup.string().required(),
})
export type CropsterIntegrationMetadata = yup.InferType<typeof cropsterIntegrationMetadataSchema>

export const mainFreightIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.MAINFREIGHT] as const)
    .required(),
  /** When true, route this integration to the Mainfreight staging API instead of production. */
  useSandbox: yup.boolean().optional(),
})
export type MainFreightIntegrationMetadata = yup.InferType<
  typeof mainFreightIntegrationMetadataSchema
>

export const tqlFreightIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.TQL_FREIGHT] as const)
    .required(),
})
export type TqlFreightIntegrationMetadata = yup.InferType<
  typeof tqlFreightIntegrationMetadataSchema
>

export const mayaIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.MAYA] as const)
    .required(),
  /** When true, route this integration to the Maya (Active Ants) test API instead of production. */
  useSandbox: yup.boolean().optional(),
})
export type MayaIntegrationMetadata = yup.InferType<typeof mayaIntegrationMetadataSchema>

export const marketTimeIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.MARKETTIME] as const)
    .required(),
  id_number: yup.string().required(),
})
export type MarketTimeIntegrationMetadata = yup.InferType<
  typeof marketTimeIntegrationMetadataSchema
>

export const mintsoftIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.MINTSOFT] as const)
    .required(),
  username: yup.string().required(),
  webhooks: yup.array(yup.string().required()).optional(),
})
export type MintsoftIntegrationMetadata = yup.InferType<typeof mintsoftIntegrationMetadataSchema>

export const navisphereIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.NAVISPHERE] as const)
    .required(),
  customerCode: yup.string().required(),
  isStaging: yup.boolean().notRequired(),
})
export type NavisphereIntegrationMetadata = yup.InferType<
  typeof navisphereIntegrationMetadataSchema
>

const tikTokShopCipherWithTtlSchema = yup.object({
  value: yup.string().required(),
  expiresAt: yup.string().required(),
})

export const tikTokShopIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.TIKTOK_SHOP] as const)
    .required(),
  shopIds: yup.array(yup.string().required()).optional(),
  open_id: yup.string().optional(),
  sellerOpenId: yup.string().optional(),
  // Legacy single-shop cache (shops[0]). Prefer shopCiphers when a shop_id is known.
  cipher: tikTokShopCipherWithTtlSchema.nullable().notRequired().default(undefined),
  // Per-shop cipher cache keyed by TikTok shop id — used when callers pass shopId.
  shopCiphers: yup
    .mixed<Record<string, yup.InferType<typeof tikTokShopCipherWithTtlSchema>>>()
    .nullable()
    .notRequired()
    .default(undefined),
})
export type TikTokShopIntegrationMetadata = yup.InferType<
  typeof tikTokShopIntegrationMetadataSchema
>

export const toastIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.TOAST] as const)
    .required(),
})
export type ToastIntegrationMetadata = yup.InferType<typeof toastIntegrationMetadataSchema>

export const airtableIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.AIRTABLE] as const)
    .required(),
  bases: yup
    .array(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
      }),
    )
    .required(),
})

export type AirtableIntegrationMetadata = yup.InferType<typeof airtableIntegrationMetadataSchema>

export const brexIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.BREX] as const)
    .required(),
  scopes: yup.array(yup.string().required()).optional(),
})

export type BrexIntegrationMetadata = yup.InferType<typeof brexIntegrationMetadataSchema>

export const beProductIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.BEPRODUCT] as const)
    .required(),
  companyId: yup.string().optional(), // ⚠️ To be determined from API
  scopes: yup.array(yup.string().required()).optional(),
  /**
   * Informational marker indicating the user-provided OAuth credentials belong to a BeProduct
   * test account rather than a production account. BeProduct's env is "account-based" — the same
   * `id.winks.io/ids` OAuth endpoints and `developers.beproduct.com` API are used for both envs;
   * which env an integration talks to is determined entirely by the `client_id`/`client_secret`
   * the PMO pasted at install time. This flag does NOT change any URL or credential resolution
   * at runtime; it only drives the row's Sandbox/Production chip for PMO awareness.
   */
  useSandbox: yup.boolean().optional(),
})
export type BeProductIntegrationMetadata = yup.InferType<typeof beProductIntegrationMetadataSchema>

export const veracoreIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.VERACORE] as const)
    .required(),
  systemId: yup.string().required(),
})
export type VeracoreIntegrationMetadata = yup.InferType<typeof veracoreIntegrationMetadataSchema>

export const shipSidekickIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SHIPSIDEKICK] as const)
    .required(),
})
export type ShipSidekickIntegrationMetadata = yup.InferType<
  typeof shipSidekickIntegrationMetadataSchema
>

export const shipStationIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.SHIPSTATION] as const)
      .required(),
    partnerAccountId: yup.string().notRequired(),
    isStaging: yup.boolean().notRequired(),
  })
  .strict()

export type ShipStationIntegrationMetadata = yup.InferType<
  typeof shipStationIntegrationMetadataSchema
>

export const silqIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SILQ] as const)
    .required(),
})
export type SilqIntegrationMetadata = yup.InferType<typeof silqIntegrationMetadataSchema>

export const usaSupplyIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.USA_SUPPLY] as const)
    .required(),
})
export type USASupplyIntegrationMetadata = yup.InferType<typeof usaSupplyIntegrationMetadataSchema>

export const walmartIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.WALMART] as const)
    .required(),
})
export type WalmartIntegrationMetadata = yup.InferType<typeof walmartIntegrationMetadataSchema>

export const misfitsMarketIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.MISFITS_MARKET] as const)
    .required(),
  /** When true, route this integration to the Misfits Market staging API instead of production. */
  useSandbox: yup.boolean().optional(),
})
export type MisfitsMarketIntegrationMetadata = yup.InferType<
  typeof misfitsMarketIntegrationMetadataSchema
>

export const unisIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.UNIS] as const)
    .required(),
  companyId: yup.string().required(),
  customerId: yup.string().required(),
  /** When true, route this integration to the UNIS staging API instead of production. */
  useSandbox: yup.boolean().optional(),
})
export type UnisIntegrationMetadata = yup.InferType<typeof unisIntegrationMetadataSchema>

export const easypostIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.EASYPOST] as const)
    .required(),
})

export type EasyPostIntegrationMetadata = yup.InferType<typeof easypostIntegrationMetadataSchema>

export const fortySeasIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.FORTY_SEAS] as const)
    .required(),
  clientId: yup.string().required(),
})

export type FortySeasIntegrationMetadata = yup.InferType<typeof fortySeasIntegrationMetadataSchema>

export const googleCloudStorageIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.GOOGLE_CLOUD_STORAGE] as const)
      .required(),
    serviceAccountEmail: yup.string().email().optional(), // Optional for custom buckets since they'll use Doss's service account
    isExternalProject: yup.boolean().optional(),
    // Fields for external projects
    bucketName: yup.string().when('isExternalProject', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  })
  .strict()

export type GoogleCloudStorageIntegrationMetadata = yup.InferType<
  typeof googleCloudStorageIntegrationMetadataSchema
>

export const nokRecommerceIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.NOK_RECOMMERCE] as const)
      .required(),
    /** When true, route this integration to the NOK Recommerce staging API instead of production. */
    useSandbox: yup.boolean().optional(),
  })
  .strict()

export type NokRecommerceIntegrationMetadata = yup.InferType<
  typeof nokRecommerceIntegrationMetadataSchema
>

export const brndSupplierIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.BRND_SUPPLIER] as const)
    .required(),
})
export type BRNDSupplierIntegrationMetadata = yup.InferType<
  typeof brndSupplierIntegrationMetadataSchema
>

export const brndWholesaleIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.BRND_WHOLESALE] as const)
    .required(),
})
export type BRNDWholesaleIntegrationMetadata = yup.InferType<
  typeof brndWholesaleIntegrationMetadataSchema
>

export const faireIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.FAIRE] as const)
    .required(),
  brandId: yup.string().optional(),
  brandName: yup.string().optional(),
})
export type FaireIntegrationMetadata = yup.InferType<typeof faireIntegrationMetadataSchema>

export const fenoIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.FENO] as const)
    .required(),
})
export type FenoIntegrationMetadata = yup.InferType<typeof fenoIntegrationMetadataSchema>

export const firstbaseIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.FIRSTBASE] as const)
    .required(),
  clientSecret: yup.string().optional(),
  accessTokenExpiry: yup.string().optional(),
  isProduction: yup.boolean().notRequired(),
})
export type FirstbaseIntegrationMetadata = yup.InferType<typeof firstbaseIntegrationMetadataSchema>

export const exchangeRatesIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.DOSS_EXCHANGE_RATES] as const)
    .required(),
})
export type ExchangeRatesIntegrationMetadata = yup.InferType<
  typeof exchangeRatesIntegrationMetadataSchema
>

export const snapfulfilIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SNAPFULFIL] as const)
    .required(),
  isProduction: yup.boolean().notRequired(),
})
export type SnapfulfilIntegrationMetadata = yup.InferType<
  typeof snapfulfilIntegrationMetadataSchema
>

export const cartonCloudIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.CARTONCLOUD] as const)
      .required(),
    customerUUID: yup.string().optional(),
    tenantUUID: yup.string().optional(),
  })
  .strict()
export type CartonCloudIntegrationMetadata = yup.InferType<
  typeof cartonCloudIntegrationMetadataSchema
>

export const medusaIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.MEDUSA] as const)
    .required(),
  baseUrl: yup.string().required(),
})
export type MedusaIntegrationMetadata = yup.InferType<typeof medusaIntegrationMetadataSchema>

export const byrdIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.BYRD] as const)
    .required(),
  apiKey: yup.string().required(),
  apiKeyPassword: yup.string().required(),
})
export type ByrdIntegrationMetadata = yup.InferType<typeof byrdIntegrationMetadataSchema>

export const westernPostIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.WESTERN_POST] as const)
    .required(),
  apiUrl: yup
    .string()
    .default('https://oms.westernfulfill.com/default/svc/web-service')
    .notRequired(),
  appKey: yup.string().required(),
  appToken: yup.string().required(),
})
export type WesternPostIntegrationMetadata = yup.InferType<
  typeof westernPostIntegrationMetadataSchema
>

export const buhoIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.BUHO] as const)
    .required(),
  /** When true, route this integration to the Buho sandbox API instead of production. */
  useSandbox: yup.boolean().optional(),
})
export type BuhoIntegrationMetadata = yup.InferType<typeof buhoIntegrationMetadataSchema>

export const shipwireIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SHIPWIRE] as const)
    .required(),
  webhooks: yup.array().of(yup.number().required()).optional(),
})
export type ShipwireIntegrationMetadata = yup.InferType<typeof shipwireIntegrationMetadataSchema>

/** A Rillet account's credentials, as nested by the first PHP-1161 release. */
const rilletNestedAccountSchema = yup.object({
  apiKey: yup.string().required(),
  webhookToken: yup.string().required(),
})

/**
 * One Rillet account per RILLET integration. An org connects its Sandbox and Production accounts
 * as separate integrations, like Locad, and each workflow step picks one (PHP-1161).
 *
 * Records saved by the first PHP-1161 release nest two accounts under `sandbox`/`production`,
 * with `isSandbox` naming the live one. {@link getRilletAccount} reads that shape too.
 * Saving from the connect dialog rewrites a record to the flat one.
 */
export const rilletIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.RILLET] as const)
      .required(),
    apiKey: yup.string().notRequired(),
    /** Whether this is a Sandbox account (`sandbox.api.rillet.com`). */
    isSandbox: yup.boolean().notRequired(),
    /**
     * Rillet-generated token that signs this account's inbound webhooks (`X-Rillet-Signature`).
     * Intake verifies against it and routes each webhook to the integrations whose token signed it.
     */
    webhookToken: yup.string().notRequired(),
    /** @deprecated First PHP-1161 release only. */
    sandbox: rilletNestedAccountSchema.notRequired().default(undefined),
    /** @deprecated First PHP-1161 release only. */
    production: rilletNestedAccountSchema.notRequired().default(undefined),
  })
  .test(
    'rillet-webhook-token',
    'Rillet needs a webhook token',
    (metadata) => getRilletAccount(metadata) !== undefined,
  )
export type RilletIntegrationMetadata = yup.InferType<typeof rilletIntegrationMetadataSchema>

/** The subset of Rillet metadata {@link getRilletAccount} reads. */
export interface RilletCredentialSource {
  isSandbox?: boolean | null
  apiKey?: string | null
  webhookToken?: string | null
  sandbox?: { apiKey?: string | null; webhookToken?: string | null } | null
  production?: { apiKey?: string | null; webhookToken?: string | null } | null
}

/**
 * The account a RILLET integration calls and verifies webhooks with, or undefined without a token.
 * `apiKey` stays optional so records saved without one still resolve (API calls then fail loudly).
 */
export function getRilletAccount(
  metadata: RilletCredentialSource,
): { apiKey?: string; webhookToken: string } | undefined {
  const nested = metadata.isSandbox === true ? metadata.sandbox : metadata.production
  if (nested?.webhookToken) {
    return { apiKey: nested.apiKey ?? undefined, webhookToken: nested.webhookToken }
  }
  if (metadata.webhookToken) {
    return { apiKey: metadata.apiKey ?? undefined, webhookToken: metadata.webhookToken }
  }
  return undefined
}

export const ntgWmsIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.NTG_WMS] as const)
    .required(),
  host: yup.string().required(),
})
export type NtgWmsIntegrationMetadata = yup.InferType<typeof ntgWmsIntegrationMetadataSchema>

export const salsifyIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.SALSIFY] as const)
    .required(),
  orgId: yup.string().required(),
})
export type SalsifyIntegrationMetadata = yup.InferType<typeof salsifyIntegrationMetadataSchema>

export const campfireIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.CAMPFIRE] as const)
    .required(),
})
export type CampfireIntegrationMetadata = yup.InferType<typeof campfireIntegrationMetadataSchema>

export const jayGroupIntegrationMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([IntegrationType.JAY_GROUP] as const)
      .required(),
    /** Jay Group sandbox/production account ID (e.g. "90463"). */
    accountId: yup.string().required(),
    /** x-division header value for DC-level partitioning (e.g. PA or NV facility). */
    division: yup.string().optional(),
    /** When true, route to stage.api.logistics.jaygroup.com; defaults to non-prod Doss deployments. */
    useSandbox: yup.boolean().optional(),
    /** Jay Group webhook registration ID — used for secret rotation. */
    webhookId: yup.string().optional(),
  })
  .strict()

export type JayGroupIntegrationMetadata = yup.InferType<typeof jayGroupIntegrationMetadataSchema>

/**
 * GoFreight gateway host for a connection.
 *
 * Full-tenant (`api.core.gofreight.co`) is the original sandbox / tenant API and remains the
 * default so existing connections keep their host. Partner Read (`partner-api.core.gofreight.co`)
 * is the production partner-scoped gateway (health + ocean-import HBL list/detail only).
 */
export enum GofreightGateway {
  FULL_TENANT = 'FULL_TENANT',
  PARTNER = 'PARTNER',
}

export const gofreightIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.GOFREIGHT] as const)
    .required(),
  useSandbox: yup.boolean().optional(),
  /**
   * Which GoFreight gateway this connection calls. Unset means full-tenant so existing
   * connections (sandbox or production keys on `api.core.gofreight.co`) do not change host.
   */
  gateway: yup.string().oneOf(Object.values(GofreightGateway)).optional(),
  /** GoFreight webhook subscription ref — used for unsubscribe on integration delete. */
  webhookRef: yup.string().optional(),
})
export type GofreightIntegrationMetadata = yup.InferType<typeof gofreightIntegrationMetadataSchema>

export const proPacIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.PRO_PAC] as const)
    .required(),
  customerId: yup.string().required(),
  /** When true, sign in and submit orders against Pro Pac `/api/v1/test/` routes. */
  useSandbox: yup.boolean().optional(),
})
export type ProPacIntegrationMetadata = yup.InferType<typeof proPacIntegrationMetadataSchema>

export const httpEndpointIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.HTTP_ENDPOINT] as const)
    .required(),
  /** https URL requests go to; an action step may append a path under it. */
  baseUrl: yup
    .string()
    .url()
    .matches(/^https:\/\//i, 'baseUrl must use https')
    .required(),
})
export type HttpEndpointIntegrationMetadata = yup.InferType<
  typeof httpEndpointIntegrationMetadataSchema
>

export const goboltIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.GOBOLT] as const)
    .required(),
  /** Overrides the GoBolt Logistics API host (production by default), e.g. for a staging host. */
  baseUrl: yup
    .string()
    .url()
    .matches(/^https:\/\//i, 'baseUrl must use https')
    .notRequired(),
  /** Sent as `ORGANIZATION-ID`; only needed when the credentials manage several organizations. */
  organizationId: yup.string().notRequired(),
})
export type GoboltIntegrationMetadata = yup.InferType<typeof goboltIntegrationMetadataSchema>

const ITS_LOGISTICS_HOST = 'its4logistics.com'

/** Whether a URL's parsed hostname is ITS's domain or one of its subdomains. */
export function isItsLogisticsHost(url: string): boolean {
  try {
    const { hostname } = new URL(url)
    return hostname === ITS_LOGISTICS_HOST || hostname.endsWith(`.${ITS_LOGISTICS_HOST}`)
  } catch {
    return false
  }
}

export const itsLogisticsIntegrationMetadataSchema = yup.object({
  type: yup
    .string()
    .oneOf([IntegrationType.ITS_LOGISTICS] as const)
    .required(),
  /**
   * Overrides the ITS Distribution API host (production by default).
   * Only ITS hosts: a run POSTs the stored password to this URL.
   */
  baseUrl: yup
    .string()
    .url()
    .matches(/^https:\/\//i, 'baseUrl must use https')
    .test(
      'its-host',
      'baseUrl must be an its4logistics.com host',
      (value) => value === undefined || value === null || isItsLogisticsHost(value),
    )
    .notRequired(),
})
export type ItsLogisticsIntegrationMetadata = yup.InferType<
  typeof itsLogisticsIntegrationMetadataSchema
>

export type IntegrationMetadata =
  | A2bFulfillmentIntegrationMetadata
  | AirtableIntegrationMetadata
  | AmazonSellerIntegrationMetadata
  | AramexIntegrationMetadata
  | BeProductIntegrationMetadata
  | BillIntegrationMetadata
  | BrexIntegrationMetadata
  | BRNDSupplierIntegrationMetadata
  | BRNDWholesaleIntegrationMetadata
  | ByrdIntegrationMetadata
  | BuhoIntegrationMetadata
  | CampfireIntegrationMetadata
  | CropsterIntegrationMetadata
  | CrstlIntegrationMetadata
  | D2IntegrationMetadata
  | DeposcoIntegrationMetadata
  | DossBankingIntegrationMetadata
  | DossBillPayIntegrationMetadata
  | EasyPostIntegrationMetadata
  | EchoShipIntegrationMetadata
  | ExchangeRatesIntegrationMetadata
  | ExtensivIntegrationMetadata
  | ExtronIntegrationMetadata
  | FaireIntegrationMetadata
  | FenoIntegrationMetadata
  | FirstbaseIntegrationMetadata
  | FlexportIntegrationMetadata
  | FortySeasIntegrationMetadata
  | FosdickIntegrationMetadata
  | FtpIntegrationMetadata
  | G2MintIntegrationMetadata
  | GmailIntegrationMetadata
  | GnosisIntegrationMetadata
  | GofreightIntegrationMetadata
  | GoogleCloudStorageIntegrationMetadata
  | NokRecommerceIntegrationMetadata
  | HubSpotIntegrationMetadata
  | IngramMicroIntegrationMetadata
  | JayGroupIntegrationMetadata
  | JazzIntegrationMetadata
  | JigsawIntegrationMetadata
  | LazadaIntegrationMetadata
  | LogiwaIntegrationMetadata
  | LocadIntegrationMetadata
  | LoopReturnsIntegrationMetadata
  | MainFreightIntegrationMetadata
  | TqlFreightIntegrationMetadata
  | MayaIntegrationMetadata
  | MarketTimeIntegrationMetadata
  | MedusaIntegrationMetadata
  | MintsoftIntegrationMetadata
  | MisfitsMarketIntegrationMetadata
  | NavisphereIntegrationMetadata
  | NetsuiteIntegrationMetadata
  | NtgWmsIntegrationMetadata
  | OrderfulIntegrationMetadata
  | OwdIntegrationMetadata
  | OutlookIntegrationMetadata
  | PostgresIntegrationMetadata
  | ProPacIntegrationMetadata
  | QuickbooksIntegrationMetadata
  | RampIntegrationMetadata
  | RapidPackIntegrationMetadata
  | ThinkLogisticsIntegrationMetadata
  | RilletIntegrationMetadata
  | RoutableIntegrationMetadata
  | SalsifyIntegrationMetadata
  | ShipBobIntegrationMetadata
  | ShipMonkIntegrationMetadata
  | ShipHeroIntegrationMetadata
  | ShipSidekickIntegrationMetadata
  | ShipStationIntegrationMetadata
  | ShipwireIntegrationMetadata
  | ShopeeIntegrationMetadata
  | ShopifyIntegrationMetadata
  | SilqIntegrationMetadata
  | SPSCommerceIntegrationMetadata
  | SnapfulfilIntegrationMetadata
  | CartonCloudIntegrationMetadata
  | StordIntegrationMetadata
  | StripeIntegrationMetadata
  | TdSynnexIntegrationMetadata
  | TdSynnexXMLIntegrationMetadata
  | TikTokShopIntegrationMetadata
  | ToastIntegrationMetadata
  | UnisIntegrationMetadata
  | USASupplyIntegrationMetadata
  | VeracoreIntegrationMetadata
  | WalmartIntegrationMetadata
  | WarehanceIntegrationMetadata
  | WesternPostIntegrationMetadata
  | WooCommerceIntegrationMetadata
  | XeroIntegrationMetadata
  | LoopReturnsIntegrationMetadata
  | HttpEndpointIntegrationMetadata
  | GoboltIntegrationMetadata
  | ItsLogisticsIntegrationMetadata

export function isIntegrationMetadata(
  metadata: Record<string, unknown>,
  type: IntegrationType,
): metadata is IntegrationMetadata {
  switch (type) {
    case IntegrationType.A2B_FULFILLMENT:
      return a2bFulfillmentIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.AIRTABLE:
      return airtableIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.AMAZON_SELLER:
      return amazonSellerIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.ARAMEX:
      return aramexIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.BEPRODUCT:
      return beProductIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.BILL:
      return billIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.BREX:
      return brexIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.BRND_SUPPLIER:
      return brndSupplierIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.BRND_WHOLESALE:
      return brndWholesaleIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.BYRD:
      return byrdIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.BUHO:
      return buhoIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.CAMPFIRE:
      return campfireIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.CROPSTER:
      return cropsterIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.CRSTL:
      return crstlIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.D2:
      return d2IntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.DEPOSCO:
      return deposcoIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.DOSS_BANKING:
      return dossBankingIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.DOSS_BILL_PAY:
      return dossBillPayIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.DOSS_EXCHANGE_RATES:
      return exchangeRatesIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.EASYPOST:
      return easypostIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.FORTY_SEAS:
      return fortySeasIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.ECHO_SHIP:
      return echoShipIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.EXTENSIV:
      return extensivIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.EXTRON:
      return extronIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.FAIRE:
      return faireIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.FENO:
      return fenoIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.FIRSTBASE:
      return firstbaseIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.FLEXPORT:
      return flexportIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.FOSDICK:
      return fosdickIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.FTP:
      return ftpIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.G2MINT:
      return g2mintIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.GMAIL:
      return gmailIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.GNOSIS:
      return gnosisIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.GOFREIGHT:
      return gofreightIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.GOOGLE_CLOUD_STORAGE:
      return googleCloudStorageIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.NOK_RECOMMERCE:
      return nokRecommerceIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.HUBSPOT:
      return hubSpotIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.INGRAM_MICRO:
      return ingramMicroIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.JAY_GROUP:
      return jayGroupIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.JAZZ:
      return jazzIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.JIGSAW:
      return jigsawIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.LAZADA:
      return lazadaIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.LOGIWA:
      return logiwaIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.MAINFREIGHT:
      return mainFreightIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.TQL_FREIGHT:
      return tqlFreightIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.MARKETTIME:
      return marketTimeIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.MAYA:
      return mayaIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.MEDUSA:
      return medusaIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.MINTSOFT:
      return mintsoftIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.MISFITS_MARKET:
      return misfitsMarketIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.NAVISPHERE:
      return navisphereIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.NETSUITE:
      return netsuiteIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.NTG_WMS:
      return ntgWmsIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.ONE_WORLD_DIRECT:
      return owdIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.ORDERFUL:
      return orderfulIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.OUTLOOK:
      return outlookIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.POSTGRES:
      return postgresIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.PRO_PAC:
      return proPacIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.QUICKBOOKS:
      return quickbooksIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.RAMP:
      return rampIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.RAPID_PACK:
      return rapidPackIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.LOCAD:
      return locadIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.THINK_LOGISTICS:
      return thinkLogisticsIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.RILLET:
      return rilletIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.ROUTABLE:
      return routableIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SALSIFY:
      return salsifyIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHIPBOB:
      return shipBobIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHIPMONK:
      return shipMonkIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHIPHERO:
      return shipHeroIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHIPSIDEKICK:
      return shipSidekickIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHIPSTATION:
      return shipStationIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHIPWIRE:
      return shipwireIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHOPEE:
      return shopeeIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SHOPIFY:
      return shopifyIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SILQ:
      return silqIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.STORD:
      return stordIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SPS_COMMERCE:
      return spsCommerceIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.SNAPFULFIL:
      return snapfulfilIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.CARTONCLOUD:
      return cartonCloudIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.STRIPE:
      return stripeIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.TD_SYNNEX:
      return tdSynnexIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.TD_SYNNEX_XML:
      return tdSynnexXMLIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.TIKTOK_SHOP:
      return tikTokShopIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.TOAST:
      return toastIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.UNIS:
      return unisIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.USA_SUPPLY:
      return usaSupplyIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.VERACORE:
      return veracoreIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.WALMART:
      return walmartIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.WAREHANCE:
      return warehanceIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.WESTERN_POST:
      return westernPostIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.WOO_COMMERCE:
      return wooCommerceIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.LOOP_RETURNS:
      return loopReturnsIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.XERO:
      return xeroIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.HTTP_ENDPOINT:
      return httpEndpointIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.GOBOLT:
      return goboltIntegrationMetadataSchema.isValidSync(metadata)
    case IntegrationType.ITS_LOGISTICS:
      return itsLogisticsIntegrationMetadataSchema.isValidSync(metadata)
  }
  return false
}
